import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useEnsuredForwardedRef, useRafLoop } from 'react-use';
import Portal from '../Portal';
import { getScrollParent, isVisible, isEqualRect, getPlacement } from './utils';

const Popper = forwardRef(function Popper({ open, anchor, style, children, onClose, ...rest }, forwardedRef) {
  const popperRef = useEnsuredForwardedRef(forwardedRef);
  const anchorRectRef = useRef(null);
  const boundaryRef = useRef(null);
  const [placement, setPlacement] = useState({ top: 0, left: 0, opacity: 0 });

  useEffect(() => {
    if (!anchor) {
      return;
    }

    boundaryRef.current = getScrollParent(anchor);
  }, [anchor]);
  const handlePlacement = useCallback(() => {
    if (!anchor || !open) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();

    if (isEqualRect(anchorRect, anchorRectRef.current)) {
      return;
    }

    const boundaryRect = boundaryRef.current?.getBoundingClientRect();

    anchorRectRef.current = anchorRect;

    if (!isVisible(anchorRect, boundaryRect)) {
      if (onClose) {
        onClose();
      }
      return;
    }

    setPlacement(getPlacement(anchorRectRef.current));
  }, [open, anchor, onClose]);

  const [stop, start, isActive] = useRafLoop(handlePlacement, open);

  useEffect(() => {
    const active = isActive();
    if (open && !active) {
      start();
    } else if (!open && active) {
      stop();
    }
  }, [stop, start, isActive, open]);

  if (!open) {
    return null;
  }

  return (
    <Portal>
      <div ref={popperRef} style={{ position: 'fixed', zIndex: 12000, ...placement, ...style }} {...rest}>
        {children}
      </div>
    </Portal>
  );
});

export default Popper;
